/**
 * FamiliarisationCustomizer - Comprehensive checklist customization component
 * Allows complete flexibility: edit/delete standard items, create custom checklists, paper mode
 */
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FileText, Settings, FileUp } from 'lucide-react';
import { Modal, ModalBody, ModalActions } from '../../common/Modal';
import { FAMILIARISATION_SECTIONS } from '../../../../config/familiarisationItems5Sections';
import { generateFamiliarisationWithCustomItems } from '../../../../utils/familiarisationHelpers';
import { useVessel } from 'contexts/VesselContext';
import { useStorageState } from '../../../../hooks/useStorageState';
import FamiliarisationPaperMode from './FamiliarisationPaperMode';
import ChecklistImportWizard from './../operations/import/ChecklistImportWizard';

const FamiliarisationCustomizer = ({ isOpen, onClose }) => {
  const { currentVesselId, getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();
  const [activeTab, setActiveTab] = useState('customize');

  // Use useStorageState hooks for vessel-scoped storage
  const { value: editedItems = {}, setValue: setEditedItems } = useStorageState('editedFamiliarisationItems', {});
  const { value: deletedItems = [], setValue: setDeletedItems } = useStorageState('deletedFamiliarisationItems', []);
  const { value: customItems = [], setValue: setCustomItems } = useStorageState('customFamiliarisationItems', []);
  const { value: paperMode = false, setValue: setPaperMode } = useStorageState('familiarisationPaperMode', false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [newItem, setNewItem] = useState({ text: '', section: 'pre-sailing', priority: 'normal' });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setHasChanges(true);
  }, [editedItems, deletedItems, customItems, paperMode]);

  // Get all items from the system
  const getAllItems = () => {
    if (!currentVessel) return [];
    
    // Generate a dummy crew member to get all possible items
    const dummyCrew = { position: 'master' };
    const familiarisation = generateFamiliarisationWithCustomItems(currentVessel, dummyCrew, []);
    
    const allItems = [];
    if (familiarisation && familiarisation.sections && Array.isArray(familiarisation.sections)) {
      familiarisation.sections.forEach(section => {
        if (section && section.items && Array.isArray(section.items)) {
          section.items.forEach(item => {
            allItems.push({
              ...item,
              sectionId: section.section,
              sectionTitle: section.title,
              isDeleted: deletedItems.includes(item.id),
              editedText: editedItems[item.id]
            });
          });
        }
      });
    }
    
    return allItems;
  };

  const handleEditItem = (itemId, newText) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: newText
    }));
  };

  const handleDeleteItem = (itemId) => {
    setDeletedItems(prev => [...prev, itemId]);
  };

  const handleRestoreItem = (itemId) => {
    setDeletedItems(prev => prev.filter(id => id !== itemId));
  };

  const handleAddCustomItem = () => {
    if (!newItem.text.trim()) return;
    
    const customItem = {
      id: `custom_${Date.now()}`,
      text: newItem.text,
      section: newItem.section,
      priority: newItem.priority,
      isCustom: true,
      roles: ['all'],
      wbc3Ref: 'Company Requirement'
    };
    
    setCustomItems(prev => [...prev, customItem]);
    setNewItem({ text: '', section: 'pre-sailing', priority: 'normal' });
    setShowAddForm(false);
  };

  const handleDeleteCustomItem = (itemId) => {
    setCustomItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleImportComplete = (importedChecklist) => {
    if (importedChecklist?.items) {
      const importedItems = importedChecklist.items.map((item, idx) => ({
        id: `imported_${Date.now()}_${idx}`,
        text: item.text,
        section: item.section || 'pre-sailing',
        priority: item.required ? 'high' : 'normal',
        isCustom: true,
        isImported: true,
        roles: ['all'],
        wbc3Ref: 'Company Requirement (Imported)'
      }));
      setCustomItems(prev => [...prev, ...importedItems]);
    }
    setShowImportWizard(false);
  };

  const handleSave = () => {
    // useStorageState hooks automatically save on setValue
    // Just save the combined customizations object for legacy compatibility
    // Note: Individual items are automatically saved by useStorageState hooks
    // All data is vessel-scoped via useStorageState

    setHasChanges(false);
    alert('Customizations saved successfully!');
  };

  const handleReset = () => {
    if (window.confirm('This will reset all customizations to default. Are you sure?')) {
      // Reset state (useStorageState will automatically clear vessel-scoped storage)
      setEditedItems({});
      setDeletedItems([]);
      setCustomItems([]);
      setPaperMode(false);

      setHasChanges(false);
    }
  };

  const allItems = getAllItems();
  const groupedItems = {};
  allItems.forEach(item => {
    if (!groupedItems[item.sectionId]) {
      groupedItems[item.sectionId] = {
        title: item.sectionTitle,
        items: []
      };
    }
    groupedItems[item.sectionId].items.push(item);
  });

  // Add custom items to grouped items
  customItems.forEach(item => {
    if (!groupedItems[item.section]) {
      const section = FAMILIARISATION_SECTIONS[item.section];
      groupedItems[item.section] = {
        title: section?.title || item.section,
        items: []
      };
    }
    groupedItems[item.section].items.push({
      ...item,
      sectionId: item.section,
      sectionTitle: groupedItems[item.section].title
    });
  });

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Customise Familiarisation Checklists" size="5xl">
      <ModalBody>
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('customize')}
              className={`whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium ${
                activeTab === 'customize'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <Settings className="inline-block h-4 w-4 mr-2" />
              Customise Items
            </button>
            <button
              onClick={() => setActiveTab('paper')}
              className={`whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium ${
                activeTab === 'paper'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <FileText className="inline-block h-4 w-4 mr-2" />
              Paper Mode Settings
            </button>
          </nav>
        </div>

        {activeTab === 'customize' && (
          <div className="space-y-6">
            {/* Add Custom Item Button */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Checklist Items</h3>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowImportWizard(true)}
                  className="inline-flex items-center gap-x-2 rounded-md border-2 border-dashed
                    border-blue-300 px-3 py-2 text-sm font-semibold text-blue-600
                    hover:bg-blue-50 transition-colors"
                >
                  <FileUp className="h-4 w-4" />
                  Import from SMS
                  <span className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">PRO</span>
                </button>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center gap-x-2 rounded-md bg-blue-600 px-3 py-2
                    text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Item
                </button>
              </div>
            </div>

            {/* Add Custom Item Form */}
            {showAddForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Add Custom Item</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Item Text</label>
                    <input
                      type="text"
                      value={newItem.text}
                      onChange={(e) => setNewItem(prev => ({ ...prev, text: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Enter the familiarisation item..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Section</label>
                      <select
                        value={newItem.section}
                        onChange={(e) => setNewItem(prev => ({ ...prev, section: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        {Object.entries(FAMILIARISATION_SECTIONS).map(([id, section]) => (
                          <option key={id} value={id}>{section.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Priority</label>
                      <select
                        value={newItem.priority}
                        onChange={(e) => setNewItem(prev => ({ ...prev, priority: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="normal">Normal</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddCustomItem}
                      className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                    >
                      Add Item
                    </button>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Items List */}
            <div className="space-y-6">
              {Object.entries(groupedItems).map(([sectionId, section]) => (
                <div key={sectionId} className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-900">{section.title}</h4>
                  <div className="space-y-2">
                    {section.items.map(item => (
                      <div
                        key={item.id}
                        className={`rounded-lg border ${
                          item.isDeleted ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
                        } p-4`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            {editedItems[item.id] !== undefined ? (
                              <input
                                type="text"
                                value={editedItems[item.id]}
                                onChange={(e) => handleEditItem(item.id, e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            ) : (
                              <p className={`text-sm ${item.isDeleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                {item.editedText || item.text}
                              </p>
                            )}
                            {item.reference && (
                              <p className="text-xs text-gray-500 mt-1">{item.reference}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              {item.isCustom && (
                                <span className="inline-flex items-center rounded-md bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                                  Company Requirement
                                </span>
                              )}
                              {item.priority && (
                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                  item.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                  item.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {item.priority}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {!item.isDeleted && !item.isCustom && (
                              <>
                                <button
                                  onClick={() => {
                                    if (editedItems[item.id] !== undefined) {
                                      const updated = { ...editedItems };
                                      delete updated[item.id];
                                      setEditedItems(updated);
                                    } else {
                                      handleEditItem(item.id, item.text);
                                    }
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                  title={editedItems[item.id] !== undefined ? "Cancel edit" : "Edit item"}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Delete item"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {item.isDeleted && !item.isCustom && (
                              <button
                                onClick={() => handleRestoreItem(item.id)}
                                className="text-green-600 hover:text-green-800 text-sm"
                              >
                                Restore
                              </button>
                            )}
                            {item.isCustom && (
                              <button
                                onClick={() => handleDeleteCustomItem(item.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete custom item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'paper' && (
          <FamiliarisationPaperMode paperMode={paperMode} setPaperMode={setPaperMode} />
        )}
      </ModalBody>

      <ModalActions>
        <button
          onClick={handleReset}
          className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Reset to Default
        </button>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
              hasChanges
                ? 'bg-blue-600 hover:bg-blue-500'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Save Changes
          </button>
        </div>
      </ModalActions>

      {/* Checklist Import Wizard */}
      <ChecklistImportWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onComplete={handleImportComplete}
        initialType="familiarisation"
      />
    </Modal>
  );
};

export default FamiliarisationCustomizer;